import { useSystemDocumentsByBqIds } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import {
  isChartSearchItem,
  isDocumentSearchItem,
  RecentItem,
  SearchableItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import {
  addRecentItem as addRecentItemToStorage,
  getRecentItems,
  getRecentItemsStorageKey,
  removeRecentItem as removeRecentItemFromStorage,
  resolveRecentItemsForActiveDocuments,
} from 'features/knowledgeDocumentViewer/utils/recentItemsStorage'
import { useWorkOrders } from 'features/knowledgeDocumentViewer/workOrders/useWorkOrder'
import {
  isCmmsRecordId,
  workOrderRowFromEntry,
} from 'features/knowledgeDocumentViewer/workOrders/workOrder'
import { useGeckoPermissions } from 'features/permissions'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'

interface UseRecentItemsReturn {
  recentItems: RecentItem[]
  addRecentItem: (item: SearchableItem) => void
}

export function useRecentItems(siteId?: string): UseRecentItemsReturn {
  const [recentItems, setRecentItems] = useState<RecentItem[]>([])
  const { user } = useGeckoPermissions()

  const userId = user?.id

  const recentDocumentIds = useMemo(() => {
    return Array.from(
      new Set(
        recentItems
          .filter((recent) => isDocumentSearchItem(recent.item))
          .map((recent) => recent.item.id)
      )
    )
  }, [recentItems])

  const { documents: activeDocuments } = useSystemDocumentsByBqIds(
    recentDocumentIds.filter((id) => !isCmmsRecordId(id))
  )
  const { entries: workOrders } = useWorkOrders(recentDocumentIds)

  // Verify recent CHARTS still exist. A Recent Chart card whose chart was
  // deleted opens a tab that can never resolve (error toast + empty chart),
  // so once the verification load reports back, prune any entry whose chart
  // id no longer has a rolodex entry — from storage and from live state.
  const recentChartIds = useMemo(() => {
    return Array.from(
      new Set(
        recentItems
          .filter((recent) => isChartSearchItem(recent.item))
          .map((recent) => recent.item.id)
      )
    )
  }, [recentItems])

  const chartLoadOptions = useMemo(
    () =>
      recentChartIds.length > 0
        ? {
            type: [RolodexEntryType.CHART],
            ids: recentChartIds,
            limit: recentChartIds.length,
          }
        : undefined,
    [recentChartIds]
  )

  const { data: chartEntryWrappers } =
    useGetRolodexEntriesByLoadOptions<Array<{ entry?: { id?: string } }>>(
      chartLoadOptions
    )

  useEffect(() => {
    if (!userId || !siteId || !chartEntryWrappers) return

    const existingChartIds = new Set(
      chartEntryWrappers
        .map((wrapper) => wrapper.entry?.id)
        .filter((id): id is string => Boolean(id))
    )
    const missingChartIds = recentChartIds.filter(
      (id) => !existingChartIds.has(id)
    )
    if (missingChartIds.length === 0) return

    const itemsBefore = getRecentItems(userId, siteId)
    let updatedItems = itemsBefore
    for (const id of missingChartIds) {
      updatedItems = removeRecentItemFromStorage(id, userId, siteId)
    }
    // Only publish an actual removal; a failed storage write returns the list
    // unchanged, and republishing it would re-trigger this effect forever.
    if (updatedItems.length !== itemsBefore.length) {
      setRecentItems(updatedItems)
    }
  }, [chartEntryWrappers, recentChartIds, userId, siteId])

  const resolvedRecentItems = useMemo(
    () =>
      resolveRecentItemsForActiveDocuments({
        recentItems,
        activeDocuments: activeDocuments ?? [],
        workOrderDocuments: workOrders.map(workOrderRowFromEntry),
      }),
    [activeDocuments, recentItems, workOrders]
  )

  const handleAddRecentItem = useCallback(
    (item: SearchableItem) => {
      if (!userId || !siteId) return

      const updatedItems = addRecentItemToStorage(item, userId, siteId)
      setRecentItems(updatedItems)
    },
    [userId, siteId]
  )

  useEffect(() => {
    if (!userId || !siteId) {
      setRecentItems([])
      return
    }

    const loadItems = () => {
      setRecentItems(getRecentItems(userId, siteId))
    }

    loadItems()

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === getRecentItemsStorageKey(userId, siteId)) {
        loadItems()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [userId, siteId])

  return {
    recentItems: resolvedRecentItems,
    addRecentItem: handleAddRecentItem,
  }
}
